import { useMemo, MouseEvent, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { OrderDetail } from "../../types";
import { TableCellProps, Typography } from "@mui/material";
import {
  Box,
  DarkTooltip,
  FlexColBox,
  Icon,
  Link,
  Table,
  TableColumn,
} from "../../common/components";
import { generatePath } from "react-router-dom";
import { ROUTES } from "../../constant/common.constant";
import { SortBy } from "../../common/components/Table/Table";
import { getReducedCartItems, isQuoteRequest } from "../../utils/CartUtils";
import { palette } from "../../styles/theme";
import { useMoment } from "../../common/hooks/useMoment";
import { ReactComponent as TechpackDownloadIcon } from "../../assets/icons/techpack_download_icon.svg";
import { ReactComponent as OrderRequestSearchIcon } from "../../assets/icons/order_request_search_icon.svg";
import AdminOrderExpandSection from "./AdminOrderExpandSection";

export interface AdminPurchasedOrdersColumn {
  width: number;
  minWidth: number;
  label: string;
  id: keyof Omit<AdminPurchasedOrdersRow, "orderDetail">;
  dataKey: string;
  field: string;
  headerName: string;
  description: string;
  sortable: boolean;
  align?: TableCellProps["align"];
}

export interface AdminPurchasedOrdersRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  orderId?: string;
  orderDate: string;
  projectNumber: string;
  numberOfItems: number;
  orderDetail: OrderDetail;
  poNumber: string;
  showDistributorTag?: boolean;
}

interface Props {
  ordersDetails: OrderDetail[];
  rowsPerPage: number;
  totalCount: number;
  isRefetching: boolean;
  isInitialLoading: boolean;
  page: number;
  handleChangePage: (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  handleChangeRowsPerPage: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSearch: (searchTerm: string) => void;
  handleSort?: (sortBy?: SortBy) => void;
  sortBy?: SortBy;
}

const AdminOrderList = ({
  ordersDetails,
  totalCount,
  rowsPerPage,
  page,
  handleChangePage,
  handleChangeRowsPerPage,
  handleSearch,
  handleSort,
  sortBy,
  isRefetching,
  isInitialLoading,
}: Props) => {
  const { t } = useTranslation();
  const { formatShortDate } = useMoment();

  const columns: TableColumn<AdminPurchasedOrdersRow>[] = [
    {
      width: "5%",
      labelKey: "",
      id: "collapsible",
      render: (row) => (
        <AdminOrderExpandSection orderDetail={row.orderDetail} />
      ),
    },
    {
      width: "20%",
      labelKey: "all_orders.project",
      id: "projectNumber",
      sortable: true,
      render: (row) => {
        return (
          <Box
            display={"flex"}
            alignItems={"center"}
            gap={"2px"}
            flexDirection={"column"}
          >
            <Typography variant="textRegular">{row.projectNumber}</Typography>
            <Typography variant="textXs">{row.orderDate}</Typography>
          </Box>
        );
      },
    },
    {
      width: "25%",
      labelKey: "all_orders.placed_by",
      id: "email",
      customSortKey: "placedBy",
      sortable: true,
      render: (row) => {
        return (
          <FlexColBox gap={"2px"}>
            <Typography variant="textRegular">
              {row.firstName} {row.lastName}
            </Typography>
            <Typography variant="textXs">{row.email}</Typography>
            {row.showDistributorTag && (
              <Box display={"flex"} alignItems={"center"} gap={"2px"}>
                <Icon icon="sell" size={12} color={palette.grey.primary} />
                <Typography variant="textXs">
                  {t("general.distributor")}
                </Typography>
              </Box>
            )}
          </FlexColBox>
        );
      },
    },
    {
      width: "20%",
      labelKey: "all_orders.po_number",
      id: "poNumber",
      sortable: true,
      render: (row) => {
        return (
          <Typography
            variant="textRegular"
            color={row.poNumber ? palette.grey.nearBlack : palette.grey.primary}
          >
            {row.poNumber ? row.poNumber : "–"}
          </Typography>
        );
      },
    },
    {
      width: "20%",
      labelKey: "all_orders.client_project_name",
      customSortKey: "clientProjectName",
      id: "clientProjectName",
      sortable: true,
      render: (row) => {
        return (
          <Typography
            variant="textRegular"
            color={
              row.orderDetail.clientDetails?.clientProjectName
                ? palette.grey.nearBlack
                : palette.grey.primary
            }
          >
            {row.orderDetail.clientDetails?.clientProjectName || "–"}
          </Typography>
        );
      },
    },
    {
      width: "10%",
      labelKey: "general.actions",
      id: "action",
      align: "right",
      render: (row) => {
        return (
          <Box display="flex" gap={1} justifyContent="flex-end" pr={"6px"}>
            <Link
              to={generatePath(ROUTES.ADMIN_ORDER, {
                orderId: row.orderDetail.preorder?.id,
              })}
              style={{
                textDecoration: "none",
                cursor: "pointer",
              }}
            >
              <DarkTooltip
                title={t("all_orders.actions.go_to_preorder")}
                placement="bottom"
              >
                <OrderRequestSearchIcon />
              </DarkTooltip>
            </Link>
            <Link
              to={generatePath(ROUTES.ADMIN_TECHPACK_ORDER, {
                orderId: row.orderDetail.preorder?.id,
              })}
              style={{
                cursor: "pointer",
                textDecoration: "none",
              }}
            >
              <DarkTooltip
                title={t("all_orders.actions.go_to_techpack")}
                placement="bottom"
              >
                <TechpackDownloadIcon />
              </DarkTooltip>
            </Link>
          </Box>
        );
      },
    },
  ];

  const rows = useMemo((): AdminPurchasedOrdersRow[] => {
    return ordersDetails.map((orderDetail) => {
      const orderId = orderDetail.preorder?.id || "";
      const isQuote = isQuoteRequest(orderDetail.pricing, orderDetail.techpack);

      const techpackProjectNumber =
        orderDetail.techpack?.projectNumber ||
        (isQuote ? t("all_orders.quote_request") : "–");

      const orderDate = formatShortDate(orderDetail.preorder?.createdAt);

      //Because of legacy when we weren't using purchasedItems, we have to use cartItems
      const numberOfItems = getReducedCartItems(
        orderDetail.pricing.comboItems
      ).length;

      return {
        id: orderId,
        email: orderDetail.user.email,
        firstName: orderDetail.user.firstName,
        lastName: orderDetail.user.lastName,
        showDistributorTag: orderDetail.isDistributor,
        orderId,
        orderDate,
        poNumber: orderDetail.poNumber || "",
        projectNumber: techpackProjectNumber,
        numberOfItems,
        orderDetail: orderDetail,
      };
    });
  }, [ordersDetails, formatShortDate, t]);

  return (
    <FlexColBox maxHeight="80vh" width="100%" height="100%">
      <Table
        columns={columns}
        data={rows}
        rowsPerPage={rowsPerPage}
        page={page}
        sortBy={sortBy}
        totalCount={totalCount}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleSearch={handleSearch}
        handleSort={handleSort}
        isRefetching={isRefetching}
        isInitialLoading={isInitialLoading}
      />
    </FlexColBox>
  );
};

export default AdminOrderList;
